import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaCheck } from 'react-icons/fa';
import ValidatedInput from './ValidatedInput';

/**
 * Componente para input de RUT chileno con validación y formateo automático
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.value - Valor actual del RUT
 * @param {function} props.onChange - Función que se ejecuta cuando cambia el valor
 * @param {string} props.placeholder - Placeholder del input
 * @param {string} props.className - Clases CSS adicionales
 * @param {boolean} props.required - Si el campo es requerido
 * @param {boolean} props.showValidation - Si mostrar indicadores de validación
 * @param {string} props.name - Nombre del input
 * @param {boolean} props.disabled - Si el input está deshabilitado
 */
const RutInput = ({
  value = '',
  onChange,
  placeholder = 'Ej: 12.345.678-9',
  className = '',
  required = false,
  showValidation = true,
  name = 'rut',
  disabled = false,
  forceShowValidation = false,
  ...props
}) => {
  const [isValid, setIsValid] = useState(null);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Función para limpiar el RUT (solo números y K)
  const cleanRut = (rut) => {
    return rut.replace(/[^0-9kK]/g, '').toUpperCase();
  };

  // Función para formatear el RUT (12.345.678-9)
  const formatRut = (rut) => {
    const cleaned = cleanRut(rut);
    
    if (cleaned.length === 0) return '';
    if (cleaned.length === 1) return cleaned;
    
    // Separar cuerpo y dígito verificador
    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    if (body.length === 0) return cleaned;
    
    // Formatear el cuerpo con puntos
    let formattedBody = '';
    for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
      if (j > 0 && j % 3 === 0) {
        formattedBody = '.' + formattedBody;
      }
      formattedBody = body[i] + formattedBody;
    }
    
    return `${formattedBody}-${dv}`;
  };

  // Función para calcular el dígito verificador
  const calculateDV = (rut) => {
    const cleaned = cleanRut(rut);
    const body = cleaned.slice(0, -1);
    
    if (body.length < 7 || body.length > 8) return null;
    
    let sum = 0;
    let multiplier = 2;
    
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const remainder = sum % 11;
    const dv = 11 - remainder;
    
    if (dv === 11) return '0';
    if (dv === 10) return 'K';
    return dv.toString();
  };

  // Función para validar el RUT completo
  const validateRut = (rut) => {
    const cleaned = cleanRut(rut);
    
    if (cleaned.length < 8 || cleaned.length > 9) return false;
    
    const providedDV = cleaned.slice(-1);
    const calculatedDV = calculateDV(cleaned);
    
    return calculatedDV === providedDV;
  };

  // Manejar cambios en el input
  const handleChange = (e) => {
    const inputValue = e.target.value;
    const cleaned = cleanRut(inputValue);
    
    // Limitar a 9 caracteres máximo (8 números + 1 DV)
    if (cleaned.length > 9) return;
    
    const formatted = formatRut(cleaned);
    
    // Actualizar validación si el RUT parece completo
    if (cleaned.length >= 8) {
      const valid = validateRut(cleaned);
      setIsValid(valid);
    } else {
      setIsValid(null);
    }
    
    // Crear evento sintético para mantener compatibilidad
    const syntheticEvent = {
      target: {
        name: name,
        value: formatted
      }
    };
    
    onChange(syntheticEvent);
  };

  // Manejar cuando el campo pierde el foco
  const handleBlur = () => {
    setHasBeenTouched(true);
    const cleaned = cleanRut(value);
    if (cleaned.length >= 8) {
      setIsValid(validateRut(cleaned));
    }
  };

  // Actualizar validación cuando cambia el valor desde el exterior
  useEffect(() => {
    const cleaned = cleanRut(value);
    if (cleaned.length >= 8) {
      setIsValid(validateRut(cleaned));
    } else {
      setIsValid(null);
    }
  }, [value]);

  // Determinar clases CSS según el estado de validación
  const getInputClasses = () => {
    let classes = `w-full bg-gray-800 border-2 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:ring-2 transition-all duration-200`;
    
    // Si hay clase de error externa (para validación manual), usarla
    if (className.includes('input-error')) {
      classes += ' input-error';
    } else if (showValidation && (hasBeenTouched || forceShowValidation)) {
      // Campo requerido vacío
      if (required && (!value || value.trim() === '')) {
        classes += ' border-red-500 focus:border-red-500 focus:ring-red-500';
      }
      // Campo con valor
      else if (value) {
        if (isValid === true) {
          classes += ' border-green-500 focus:border-green-500 focus:ring-green-500';
        } else if (isValid === false) {
          classes += ' border-red-500 focus:border-red-500 focus:ring-red-500';
        } else {
          classes += ' border-gray-700 focus:border-blue-500 focus:ring-blue-500';
        }
      } else {
        classes += ' border-gray-700 focus:border-blue-500 focus:ring-blue-500';
      }
    } else {
      classes += ' border-gray-700 focus:border-blue-500 focus:ring-blue-500';
    }
    
    // Agregar clases adicionales que no sean input-error
    const additionalClasses = className.replace('input-error', '').trim();
    if (additionalClasses) {
      classes += ` ${additionalClasses}`;
    }
    
    return classes;
  };

  return (
    <div className="relative">
      <div className="relative">
        <ValidatedInput
          type="text"
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={getInputClasses()}
          required={required}
          disabled={disabled}
          maxLength={12} // 12.345.678-9 = 12 caracteres con formato
          {...props}
        />
        
        {/* Indicador de validación */}
        {showValidation && (hasBeenTouched || forceShowValidation) && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            {/* Campo requerido vacío */}
            {required && (!value || value.trim() === '') && (
              <FaExclamationTriangle className="text-red-500 text-sm" />
            )}
            {/* Campo con valor */}
            {value && value.trim() !== '' && (
              <>
                {isValid === true && (
                  <FaCheck className="text-green-500 text-sm" />
                )}
                {isValid === false && (
                  <FaExclamationTriangle className="text-red-500 text-sm" />
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Mensaje de error */}
      {showValidation && (hasBeenTouched || forceShowValidation) && (
        <>
          {/* Error por campo requerido vacío */}
          {required && (!value || value.trim() === '') && (
            <p className="mt-1 text-sm text-red-400 flex items-center">
              <FaExclamationTriangle className="mr-1 text-xs" />
              El RUT es requerido
            </p>
          )}
          {/* Error por formato inválido */}
          {value && value.trim() !== '' && isValid === false && (
            <p className="mt-1 text-sm text-red-400 flex items-center">
              <FaExclamationTriangle className="mr-1 text-xs" />
              RUT inválido. Verifique el número y dígito verificador.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default RutInput;